import React, { useEffect, useMemo, useState } from "react";
import { FiSave } from "react-icons/fi";

import Representatives from "./Representatives";

import {
  getRepresentativeDailyData,
  updateDailyShipping,
} from "./RepresentativeSettlementService";

const RepresentativeSettlementPage = ({
  selectedRepresentative,
  onSelectRepresentative,
  selectedDate,
  onDateChange,
  onDataLoaded,
  refreshKey,
}) => {
  const [dailyData, setDailyData] = useState(null);
  const [dailyShipping, setDailyShipping] = useState(0);
  const [shippingValues, setShippingValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingShipping, setSavingShipping] = useState(false);

const invoices = dailyData?.invoices || [];

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString("ar-EG");
  };

  const loadDailyData = async () => {
    if (!selectedRepresentative?.id || !selectedDate) {
      setDailyData(null);
      onDataLoaded?.(null);
      return;
    }

    try {
      setLoading(true);

      const response = await getRepresentativeDailyData(
        selectedRepresentative.id,
        selectedDate
      );

      const result = response?.data ?? response;

      const normalizedData = Array.isArray(result)
        ? result[0]
        : result;

      setDailyData(normalizedData || null);

      onDataLoaded?.(normalizedData || null);
      // الشحن العام لليومية
      setDailyShipping(Number(normalizedData?.shipping) || 0);
      // الشحن الخاص بكل فاتورة
      const shippingMap = {};
      (normalizedData?.invoices || []).forEach((invoice) => {
        shippingMap[invoice.id] = Number(invoice.shipping) || 0;
      });
      setShippingValues(shippingMap);
    } catch (error) {
      console.error("Daily data error:", error);

      setDailyData(null);
      onDataLoaded?.(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDailyData();
  }, [
    selectedRepresentative?.id,
    selectedDate,
    refreshKey,
  ]);

const handleShippingChange = (invoiceId, value) => {
  setShippingValues((previous) => ({
    ...previous,
    [invoiceId]: value,
  }));
};

const handleDailyShippingChange = (value) => {
  setDailyShipping(value);

  const newShippingValues = {};

  invoices.forEach((invoice) => {
    newShippingValues[invoice.id] = value;
  });

  setShippingValues(newShippingValues);
};

  const handleSaveShipping = async () => {
    if (!selectedRepresentative?.id || !invoices.length) {
      return;
    }

    try {
      setSavingShipping(true);

      const invoicesShipping = invoices.map((invoice) => ({
        invoiceId: invoice.id,
        shipping: Number(
          shippingValues[invoice.id] ?? invoice.shipping ?? 0
        ),
      }));

      await updateDailyShipping({
        representativeId: selectedRepresentative.id,
        dailyDate: `${selectedDate}T00:00:00`,
        shipping: Number(dailyShipping) || 0,
        invoicesShipping,
      });

      await loadDailyData();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingShipping(false);
    }
  };


const totals = useMemo(() => {
  let totalCount = 0;

  // إجمالي القيمة قبل خصم الشحن
  let totalValue = 0;

  // إجمالي الشحن
  let totalShipping = 0;

  // النهائي بعد خصم الشحن
  let finalTotal = 0;

  invoices.forEach((invoice) => {
    const shipping = Number(
      shippingValues[invoice.id] ??
        invoice.shipping ??
        0
    );

    const totalPrice = Number(
      invoice.totalPrice || 0
    );

    totalCount += invoice.items?.length || 0;

    totalValue += totalPrice;

    totalShipping += shipping;

    finalTotal += totalPrice - shipping;
  });

  return {
    totalCount,
    totalValue,
    totalShipping,
    finalTotal,
  };
}, [invoices, shippingValues]);


  const arabicDate = selectedDate
    ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString(
        "ar-EG"
      )
    : "";

  const arabicDay = selectedDate
    ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString(
        "ar-EG",
        {
          weekday: "long",
        }
      )
    : "";

  return (
    <>
      <Representatives
        selectedRepresentative={selectedRepresentative}
        onSelectRepresentative={onSelectRepresentative}
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        onRefresh={loadDailyData}
      />

      <div className="representative-report">
        <div className="report-header">
          <h2 className="report-title">
            كشف حساب المندوب
          </h2>

          <div className="report-info">
            <div className="report-info-item">
              المندوب /{" "}
              <strong>
                {selectedRepresentative?.fullName || "-"}
              </strong>
            </div>

            <div className="report-info-item">
              التاريخ /{" "}
              <strong>{arabicDate || "-"}</strong>
            </div>

            <div className="report-info-item">
              اليوم /{" "}
              <strong>{arabicDay || "-"}</strong>
            </div>
          </div>
        </div>

        {!selectedRepresentative ? (
          <div className="report-empty">
            من فضلك اختر المندوب
          </div>
        ) : loading ? (
          <div className="report-loading">
            جاري تحميل بيانات المندوب...
          </div>
        ) : (
          <>
            <div className="invoice-count">
              <span>عدد الفواتير</span>

              <strong>{invoices.length}</strong>
            </div>

            <div className="table-wrapper">
              <table className="representative-table">
                <thead>
                  <tr>
                    <th>الفهرس</th>
                    <th>كود الفاتورة</th>
                    <th>بيان الاصناف</th>
                    <th>عدد الاصناف</th>
                    <th>
                      <div className="shipping-header">
                        <span>الشحن</span>

                        <input
                          className="shipping-input shipping-global-input"
                          type="number"
                          min="0"
                          value={dailyShipping}
                          onChange={(event) =>
                            handleDailyShippingChange(event.target.value)
                          }
                          placeholder="الكل"
                        />
                      </div>
                    </th>
                    <th>القيمة</th>
                    <th>الاجمالي</th>
                    <th>المحافظة</th>
                    <th>الحالة</th>
                  </tr>
                </thead>

                <tbody>
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan="9">
                        لا توجد فواتير في هذا التاريخ
                      </td>
                    </tr>
                  ) : (
                    invoices.map((invoice, index) => {
                      const shipping = Number(
                        shippingValues[invoice.id] ??
                          invoice.shipping ??
                          0
                      );

                      const totalPrice = Number(
                        invoice.totalPrice || 0
                      );

                      const OverTotalPrice = Number(
                        invoice.overTotalPrice || 0
                      );

                      const value = totalPrice - shipping;

                      const itemsNames =
                        invoice.items
                          ?.map(
                            (item) =>
                              item?.category?.name || ""
                          )
                          .filter(Boolean)
                          .join("، ") || "-";

                      return (
                        <tr key={invoice.id}>
                          <td>{index + 1}</td>

                          <td>
                            {invoice.invoiceCode || "-"}
                          </td>

                          <td className="items-cell">
                            {itemsNames}
                          </td>

                          <td>
                            {invoice.items?.length || 0}
                          </td>

                          <td>
                            <input
                              className="shipping-input"
                              type="number"
                              min="0"
                              value={shipping}
                              onChange={(event) =>
                                handleShippingChange(
                                  invoice.id,
                                  event.target.value
                                )
                              }
                            />
                          </td>


                          <td>
                            {formatNumber(value)}
                          </td>

                          <td>
                            {formatNumber(OverTotalPrice)}
                          </td>

                          <td>
                            {invoice?.governorate?.name ||
                              "-"}
                          </td>

                          <td>
                            {invoice.status || "-"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {invoices.length > 0 && (
              <>
                <div className="save-shipping-wrapper no-print">
                  <button
                    type="button"
                    className="save-shipping-button"
                    onClick={handleSaveShipping}
                    disabled={savingShipping}
                  >
                    <FiSave size={15} />

                    {savingShipping
                      ? "جاري الحفظ..."
                      : "حفظ الشحن"}
                  </button>
                </div>

                <div className="summary-wrapper">
                  <table className="summary-table">
                    <thead>
                      <tr>
                        <th>إجمالي العدد</th>
                        <th>إجمالي القيمة</th>
                        <th>إجمالي الشحن</th>
                        <th>الإجمالي النهائي</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>
                          {formatNumber(dailyData?.totalNumberOfCategory || 0)}
                        </td>

                        <td>
                          {formatNumber(dailyData?.totalPriceSum || 0)}
                        </td>

                        <td>
                          {formatNumber(dailyData?.totalShipping || 0)}
                        </td>

                        <td>
                          {formatNumber(dailyData?.netFinalTotal || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default RepresentativeSettlementPage;